import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, Phone, Mail, MapPin } from "lucide-react";
import { useB2BSettings } from "../../lib/b2b";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/piani", label: "Piani" },
  { to: "/cosa-controlliamo", label: "Cosa controlliamo" },
  { to: "/property-manager", label: "Property manager" },
  { to: "/contatti", label: "Contatti" },
];

export function B2BLayout({ children }) {
  const s = useB2BSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const launched = s?.launch?.launched;
  const c = s?.contacts || {};

  return (
    <div className="min-h-screen bg-[#F4F7FB] text-[#0B1F3A]">
      {s && !launched && (
        <div data-testid="b2b-launch-banner" className="bg-[#0E7C7B] px-4 py-2 text-center text-xs font-semibold text-white sm:text-sm">
          In arrivo — lancio {s.launch?.label || "prossimamente"}. {s.launch?.note || "Le richieste di proposta sono già aperte."}
        </div>
      )}

      <header className="sticky top-0 z-40 border-b border-[#0B1F3A]/10 bg-white/90 backdrop-blur-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <Link to="/" data-testid="b2b-logo" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0B1F3A] text-white"><ShieldCheck className="h-5 w-5 text-[#0E7C7B]" /></div>
            <span className="font-display text-xl font-extrabold tracking-tight">ProntoSof<span className="text-[#F97316]">24</span></span>
          </Link>
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`b2b-nav-${n.to}`}
                className={`text-sm font-semibold transition hover:text-[#0E7C7B] ${loc.pathname === n.to ? "text-[#0E7C7B]" : "text-[#0B1F3A]/70"}`}>{n.label}</Link>
            ))}
            <Link to="/login" className="text-sm font-semibold text-[#0B1F3A]/50 transition hover:text-[#0B1F3A]">Area clienti</Link>
            <button data-testid="b2b-cta-header" onClick={() => nav("/contatti")}
              className="rounded-full bg-[#F97316] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#F97316]/25 transition hover:brightness-110 active:scale-[0.98]">Richiedi una proposta</button>
          </nav>
          <button data-testid="b2b-menu-toggle" onClick={() => setOpen(!open)} className="rounded-lg p-2 lg:hidden">{open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
        </div>
        {open && (
          <div className="border-t border-[#0B1F3A]/10 bg-white px-5 py-3 lg:hidden">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`b2b-mnav-${n.to}`} onClick={() => setOpen(false)} className="block py-2.5 text-sm font-semibold text-[#0B1F3A]/80">{n.label}</Link>
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="block py-2.5 text-sm font-semibold text-[#0B1F3A]/50">Area clienti</Link>
            <button data-testid="b2b-cta-mobile" onClick={() => { setOpen(false); nav("/contatti"); }} className="mt-2 w-full rounded-full bg-[#F97316] px-5 py-3 text-sm font-bold text-white">Richiedi una proposta</button>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="mt-16 bg-[#0B1F3A] px-5 py-12 text-white/80">
        <div className="mx-auto grid max-w-6xl gap-8 sm:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 text-white"><ShieldCheck className="h-5 w-5 text-[#0E7C7B]" /><span className="font-display text-lg font-extrabold">ProntoSof<span className="text-[#F97316]">24</span></span></div>
            <p className="mt-3 text-sm leading-relaxed text-white/60">Manutenzione programmata in abbonamento per host, property manager e strutture ricettive a Roma.</p>
          </div>
          <div className="text-sm">
            <p className="mb-3 font-bold text-white">Contatti</p>
            <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#0E7C7B]" />{c.phone || "Telefono: da definire"}</p>
            <p className="mt-2 flex items-center gap-2"><Mail className="h-4 w-4 text-[#0E7C7B]" />{c.email || "Email: da definire"}</p>
            <p className="mt-2 flex items-center gap-2"><MapPin className="h-4 w-4 text-[#0E7C7B]" />{c.address || "Sede: da definire"}</p>
            {c.vat && <p className="mt-2 text-white/50">P.IVA {c.vat}</p>}
          </div>
          <div className="text-sm">
            <p className="mb-3 font-bold text-white">Informazioni</p>
            <Link to="/piani" className="block py-1 text-white/60 hover:text-white">Piani di abbonamento</Link>
            <Link to="/cosa-controlliamo" className="block py-1 text-white/60 hover:text-white">Cosa controlliamo</Link>
            <Link to="/privacy" className="block py-1 text-white/60 hover:text-white">Informativa privacy</Link>
          </div>
        </div>
        <p className="mx-auto mt-8 max-w-6xl border-t border-white/10 pt-6 text-xs text-white/40">
          © {new Date().getFullYear()} ProntoSof24. I dati societari completi verranno pubblicati all'attivazione del servizio.
        </p>
      </footer>
    </div>
  );
}
